import React, { useState, useEffect } from 'react';

const CountdownTimer = ({ targetDate, title, message }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  // Function to format title with superscript ordinal
  const formatTitleWithSuperscript = (title) => {
    // Match patterns like "1st", "2nd", "3rd", "4th", "5th", etc.
    const regex = /(\d+)(st|nd|rd|th)/i;
    const match = title.match(regex);

    if (match) {
      const number = match[1];
      const suffix = match[2];
      const before = title.substring(0, match.index);
      const after = title.substring(match.index + match[0].length);

      return (
        <>
          {before}
          {number}
          <sup className="text-xs align-super">{suffix}</sup>
          {after}
        </>
      );
    }
    return title;
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  const formatNumber = (num) => {
    return num.toString().padStart(2, '0');
  };

  const formattedTitle = formatTitleWithSuperscript(title);

  return (
    <div className="py-12" style={{ backgroundColor: '#FEFBEC' }}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Timer Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Title Section inside Timer */}
            <div className="bg-gradient-to-r from-primary to-primary-dark py-4 px-6">
              <h2 className="text-xl md:text-2xl font-bold text-white text-center">
                {formattedTitle}
              </h2>
              <p className="text-white/80 text-center text-sm mt-1">
                {message}
              </p>
            </div>

            {/* Countdown Numbers */}
            <div className="p-4 md:p-8">
              <div className="grid grid-cols-4 gap-2 md:gap-4">
                {/* Days */}
                <div className="text-center">
                  <div className="bg-gray-50 rounded-lg p-2 md:p-4 border border-gray-200">
                    <div className="text-xl md:text-3xl font-bold text-primary">
                      {formatNumber(timeLeft.days)}
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500 uppercase mt-1 tracking-wider">
                      DAYS
                    </div>
                  </div>
                </div>

                {/* Hours */}
                <div className="text-center">
                  <div className="bg-gray-50 rounded-lg p-2 md:p-4 border border-gray-200">
                    <div className="text-xl md:text-3xl font-bold text-primary">
                      {formatNumber(timeLeft.hours)}
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500 uppercase mt-1 tracking-wider">
                      HRS
                    </div>
                  </div>
                </div>

                {/* Minutes */}
                <div className="text-center">
                  <div className="bg-gray-50 rounded-lg p-2 md:p-4 border border-gray-200">
                    <div className="text-xl md:text-3xl font-bold text-primary">
                      {formatNumber(timeLeft.minutes)}
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500 uppercase mt-1 tracking-wider">
                      MINS
                    </div>
                  </div>
                </div>

                {/* Seconds */}
                <div className="text-center">
                  <div className="bg-gray-50 rounded-lg p-2 md:p-4 border border-gray-200">
                    <div className="text-xl md:text-3xl font-bold text-primary">
                      {formatNumber(timeLeft.seconds)}
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500 uppercase mt-1 tracking-wider">
                      SECS
                    </div>
                  </div>
                </div>
              </div>

              {/* Zero Message */}
              {timeLeft.days === 0 && timeLeft.hours === 0 &&
               timeLeft.minutes === 0 && timeLeft.seconds === 0 && (
                <div className="mt-6 text-center">
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    <p className="font-semibold">🎉 The Graduation Day is Here! 🎉</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
